import { yupResolver } from '@hookform/resolvers/yup';
import {
  PAGE_DEFAULT,
  SORT_ATTRIBUTE_PAYMENTS_DEFAULT,
  SORT_ORDER_DEFAULT
} from 'constants/defaults';
import {
  InputMaybe,
  PaymentPage,
  PaymentSortAttributeEnum,
  QueryPaymentsArgs,
  SortOrderEnum
} from 'gql/graphql';
import { usePathname, useSearchParams } from 'next/navigation';
import { usePayments } from 'providers/PaymentsProviders/usePayments';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { extractParam } from 'utils/paramsUtil';
import * as yup from 'yup';

export type FiltersPaymentsForm = Omit<QueryPaymentsArgs, 'page' | 'perPage' | 'skipPagination'>;

interface IUseSearchParamsPaymentsOut {
  payments?: PaymentPage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersPaymentsForm>;
  filtersAdded: boolean;
  sortAttribute?: InputMaybe<PaymentSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
}

export default function useSearchParamsPayments(): IUseSearchParamsPaymentsOut {
  const schema = yup.object().shape({} as TScheme<FiltersPaymentsForm>);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersPaymentsForm>({
    resolver: yupResolver(schema)
  });

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_PAYMENTS_DEFAULT
  );

  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);

  const sortAttribute = useWatch<FiltersPaymentsForm>({
    control,
    name: KeysEnum.SortAttribute,
    defaultValue: SORT_ATTRIBUTE_PAYMENTS_DEFAULT
  }) as InputMaybe<PaymentSortAttributeEnum>;

  const sortOrder = useWatch<FiltersPaymentsForm>({
    control,
    name: KeysEnum.SortOrder,
    defaultValue: SORT_ORDER_DEFAULT
  }) as InputMaybe<SortOrderEnum>;

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        sortOrderInitial !== SORT_ORDER_DEFAULT,
        sortAttributeInitial !== SORT_ATTRIBUTE_PAYMENTS_DEFAULT
      ].some(Boolean),
    [page, sortAttributeInitial, sortOrderInitial]
  );

  const input: QueryPaymentsArgs = {
    sortAttribute,
    sortOrder,
    page
  };

  const { data: payments, isLoading, isPending } = usePayments(input);

  const totalPages = payments?.pagesCount ?? 0;

  const isRetrieving = isLoading && page === 1;

  const resetFilters = useCallback(() => {
    setPage(pageInitial);
    setValue(KeysEnum.SortAttribute, sortAttributeInitial);
    setValue(KeysEnum.SortOrder, sortOrderInitial);
  }, [pageInitial, sortAttributeInitial, sortOrderInitial, setValue]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.SortAttribute);
      url.searchParams.delete(KeysEnum.SortOrder);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (sortOrder && sortAttribute)
        url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (sortAttribute && sortOrder)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      url.searchParams.delete(KeysEnum.Page);
      setPage([sortOrder && sortAttribute].some(Boolean) && isDirty ? PAGE_DEFAULT : pageInitial);

      window.history.replaceState({}, '', url.toString());
    }
  }, [pageInitial, sortOrder, sortAttribute, isDirty]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname]);

  return {
    payments,
    isLoading,
    isPending,
    isRetrieving,
    page,
    setPage,
    totalPages,
    handleClear,
    control,
    filtersAdded,
    sortAttribute,
    sortOrder
  };
}
